import { InputPassword } from '@repo/ui/commons/input-password'
import { Field, FieldDescription, FieldError, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues
} from 'react-hook-form'

export function AuthTextField<T extends FieldValues>(props: {
	autoComplete: string
	control: Control<T>
	name: FieldPath<T>
	description?: string
	label: string
	placeholder?: string
	type?: string
}) {
	return (
		<Controller
			control={props.control}
			name={props.name}
			render={({ field, fieldState }) => {
				const descriptionId = `${field.name}-description`
				const errorId = `${field.name}-error`
				const describedBy = getFieldDescriptionId({
					description: props.description,
					descriptionId,
					error: fieldState.error,
					errorId
				})

				return (
					<Field
						className='gap-1.5'
						data-invalid={fieldState.invalid}
					>
						<FieldLabel
							className='font-semibold text-muted-foreground text-xs uppercase'
							htmlFor={field.name}
						>
							{props.label}
						</FieldLabel>
						{props.type === 'password' ? (
							<InputPassword
								{...field}
								aria-describedby={describedBy}
								aria-invalid={fieldState.invalid}
								autoComplete={props.autoComplete}
								className='h-11 bg-background'
								id={field.name}
								placeholder={props.placeholder}
							/>
						) : (
							<Input
								{...field}
								aria-describedby={describedBy}
								aria-invalid={fieldState.invalid}
								autoComplete={props.autoComplete}
								className='h-11 bg-background px-3.5 text-[15px]'
								id={field.name}
								placeholder={props.placeholder}
								type={props.type ?? 'text'}
							/>
						)}
						{fieldState.error ? (
							<FieldError
								errors={[fieldState.error]}
								id={errorId}
							/>
						) : null}
						{!fieldState.error && props.description ? (
							<FieldDescription id={descriptionId}>
								{props.description}
							</FieldDescription>
						) : null}
					</Field>
				)
			}}
		/>
	)
}

function getFieldDescriptionId(props: {
	description?: string
	descriptionId: string
	error?: { message?: string }
	errorId: string
}) {
	if (props.error) {
		return props.errorId
	}
	if (props.description) {
		return props.descriptionId
	}
	return undefined
}
